import { C } from './constants';

/* ══════════════════════════════════════════════════════════════
   LEARNING PATH
   Presentation for the path on /dashboard. Every `id` here must
   exist in Backend/utils/roadmap.js — the server owns whether a
   node is done, this file only owns how it looks and where it
   sends you.

   Order is deliberate: read hiragana → read katakana → words you
   can now read → the kanji behind those words → sentences → exam.
══════════════════════════════════════════════════════════════ */

export const SECTIONS = [
  {
    id: 'hiragana',
    kicker: 'Section 1',
    title: 'Hiragana',
    sub: 'The alphabet everything else is built on.',
    color: C.accent,
    glyph: 'あ',
    nodes: [
      { id: 'hira-vowels',     title: 'Vowels',        sub: 'あ い う え お', icon: 'あ', script: 'hiragana', groups: ['vowels'] },
      { id: 'hira-k',          title: 'K row',         sub: 'か き く け こ', icon: 'か', script: 'hiragana', groups: ['k'] },
      { id: 'hira-s',          title: 'S row',         sub: 'さ し す せ そ', icon: 'さ', script: 'hiragana', groups: ['s'] },
      { id: 'hira-t',          title: 'T row',         sub: 'た ち つ て と', icon: 'た', script: 'hiragana', groups: ['t'] },
      { id: 'hira-n',          title: 'N row',         sub: 'な に ぬ ね の', icon: 'な', script: 'hiragana', groups: ['n'] },
      { id: 'hira-h',          title: 'H row',         sub: 'は ひ ふ へ ほ', icon: 'は', script: 'hiragana', groups: ['h'] },
      { id: 'hira-m',          title: 'M row',         sub: 'ま み む め も', icon: 'ま', script: 'hiragana', groups: ['m'] },
      { id: 'hira-r',          title: 'R row',         sub: 'ら り る れ ろ', icon: 'ら', script: 'hiragana', groups: ['r'] },
      { id: 'hira-ywn',        title: 'Y, W & ん',     sub: 'や ゆ よ わ を ん', icon: 'や', script: 'hiragana', groups: ['y', 'w', 'special'] },
      { id: 'hira-dakuten',    title: 'Dakuten',       sub: 'Voiced — が ざ だ ば', icon: 'が', script: 'hiragana', groups: ['dakuten'] },
      { id: 'hira-handakuten', title: 'Handakuten',    sub: 'Semi-voiced — ぱ', icon: 'ぱ', script: 'hiragana', groups: ['handakuten'] },
      { id: 'hira-boss',       title: 'Hiragana Boss', sub: 'Every row, one run · 80%', icon: '鬼', script: 'hiragana', groups: ['vowels', 'k', 's', 't', 'n', 'h', 'm', 'r', 'y', 'w', 'special', 'dakuten', 'handakuten'], boss: true },
    ],
  },
  {
    id: 'katakana',
    kicker: 'Section 2',
    title: 'Katakana',
    sub: 'For loanwords, names and emphasis.',
    color: C.blue,
    glyph: 'ア',
    nodes: [
      { id: 'kata-vowels',     title: 'Vowels',     sub: 'ア イ ウ エ オ', icon: 'ア', script: 'katakana', groups: ['vowels'] },
      { id: 'kata-k',          title: 'K row',      sub: 'カ キ ク ケ コ', icon: 'カ', script: 'katakana', groups: ['k'] },
      { id: 'kata-s',          title: 'S row',      sub: 'サ シ ス セ ソ', icon: 'サ', script: 'katakana', groups: ['s'] },
      { id: 'kata-t',          title: 'T row',      sub: 'タ チ ツ テ ト', icon: 'タ', script: 'katakana', groups: ['t'] },
      { id: 'kata-n',          title: 'N row',      sub: 'ナ ニ ヌ ネ ノ', icon: 'ナ', script: 'katakana', groups: ['n'] },
      { id: 'kata-h',          title: 'H row',      sub: 'ハ ヒ フ ヘ ホ', icon: 'ハ', script: 'katakana', groups: ['h'] },
      { id: 'kata-m',          title: 'M row',      sub: 'マ ミ ム メ モ', icon: 'マ', script: 'katakana', groups: ['m'] },
      { id: 'kata-r',          title: 'R row',      sub: 'ラ リ ル レ ロ', icon: 'ラ', script: 'katakana', groups: ['r'] },
      { id: 'kata-ywn',        title: 'Y, W & ン',  sub: 'ヤ ユ ヨ ワ ヲ ン', icon: 'ヤ', script: 'katakana', groups: ['y', 'w', 'special'] },
      { id: 'kata-dakuten',    title: 'Dakuten',    sub: 'Voiced — ガ ザ ダ バ', icon: 'ガ', script: 'katakana', groups: ['dakuten'] },
      { id: 'kata-handakuten', title: 'Handakuten', sub: 'Semi-voiced — パ', icon: 'パ', script: 'katakana', groups: ['handakuten'] },
      { id: 'kata-boss',       title: 'Katakana Boss', sub: 'Every row, one run · 80%', icon: '鬼', script: 'katakana', groups: ['vowels', 'k', 's', 't', 'n', 'h', 'm', 'r', 'y', 'w', 'special', 'dakuten', 'handakuten'], boss: true },
    ],
  },
  {
    id: 'words',
    kicker: 'Section 3',
    title: 'First Words',
    sub: 'Now that you can read, build a word bank.',
    color: C.green,
    glyph: '語',
    route: '/flashcards',
    nodes: [
      { id: 'words-first',      title: 'First 10 Words', sub: 'Get the habit going',       icon: '語' },
      { id: 'words-nouns',      title: 'Everyday Nouns', sub: 'People, places, things',    icon: '本' },
      { id: 'words-pronouns',   title: 'Pronouns',       sub: 'わたし, あなた, これ',       icon: '私' },
      { id: 'words-numbers',    title: 'Numbers',        sub: 'Counting and quantities',   icon: '数' },
      { id: 'words-verbs',      title: 'Common Verbs',   sub: 'Actions you use daily',     icon: '行' },
      { id: 'words-adjectives', title: 'Adjectives',     sub: 'Describing things',         icon: '大' },
      { id: 'words-checkpoint', title: 'Word Boss',       sub: '60 words mastered',        icon: '鬼', boss: true },
    ],
  },
  {
    id: 'kanji',
    kicker: 'Section 4',
    title: 'First Kanji',
    sub: 'The characters behind the words you know.',
    color: C.yellow,
    glyph: '漢',
    route: '/kanji',
    nodes: [
      { id: 'kanji-numbers',    title: 'Numbers',        sub: '一 二 三 … 十',    icon: '一' },
      { id: 'kanji-days',       title: 'Days & Nature',  sub: '日 月 火 水 木 金 土', icon: '日' },
      { id: 'kanji-time',       title: 'Telling Time',   sub: '年 時 分 間 今 午',  icon: '時' },
      { id: 'kanji-people',     title: 'People',         sub: '人 女 男 子 友 父 母', icon: '人' },
      { id: 'kanji-position',   title: 'Size & Position', sub: '大 小 上 下 中 外',  icon: '大' },
      { id: 'kanji-places',     title: 'Places',         sub: '山 川 国 語 学 校',  icon: '山' },
      { id: 'kanji-checkpoint', title: 'Kanji Boss',       sub: '40 kanji mastered', icon: '鬼', boss: true },
    ],
  },
  {
    id: 'sentences',
    kicker: 'Section 5',
    title: 'Sentences',
    sub: 'Put your words together properly.',
    color: C.purple,
    glyph: '文',
    route: '/grammar',
    nodes: [
      { id: 'grammar-basics',    title: 'Grammar Basics', sub: 'Sentence shape',        icon: '文' },
      { id: 'grammar-particles', title: 'Particles',      sub: 'は・が・を・に',        icon: 'は' },
      { id: 'grammar-quiz',      title: 'Grammar Check',  sub: 'Score 60% on a quiz',   icon: '問', route: '/quiz' },
      { id: 'grammar-boss',      title: 'Sentence Boss',  sub: '2 quizzes at 80%',      icon: '鬼', route: '/quiz', boss: true },
    ],
  },
  {
    id: 'exam',
    kicker: 'Section 6',
    title: 'N5 Exam Prep',
    sub: 'Turn what you know into a pass.',
    color: C.red,
    glyph: 'N5',
    route: '/quiz',
    nodes: [
      { id: 'n5-mixed',    title: 'Mixed Quizzes', sub: '3 quizzes at 70%+',   icon: '問' },
      { id: 'n5-practice', title: 'Practice Test', sub: 'Full mock exam',      icon: '表', route: '/practice-tests' },
      { id: 'n5-final',    title: 'N5 Boss',       sub: '5 quizzes at 80%+',   icon: '鬼', boss: true },
    ],
  },
];

export const ALL_NODES = SECTIONS.flatMap(s =>
  s.nodes.map(n => ({ ...n, sectionId: s.id, color: s.color, sectionTitle: s.title }))
);

export const NODE_BY_ID = Object.fromEntries(ALL_NODES.map(n => [n.id, n]));

/* Every kana row that comes before this one in the same script. A review
   stage practises its own row *plus* all of these, which is the whole point
   of the stage: without it you learn あ-row in week one and have quietly
   forgotten it by the time you reach ま-row. */
export function priorGroups(nodeId) {
  const node = NODE_BY_ID[nodeId];
  if (!node?.script) return [];
  const section = SECTIONS.find(s => s.id === node.sectionId);
  if (!section) return [];

  const index = section.nodes.findIndex(n => n.id === nodeId);
  return section.nodes
    .slice(0, Math.max(0, index))
    .flatMap(n => n.groups || []);
}

/* Where a node sends you. A kana node deep-links to one *stage* of one row:
   the exact script, the exact set of groups to draw cards from, and the
   exercise that stage is. `focus=1` means "this was launched from the path"
   — the exercise opens full screen and closing it returns to the path
   rather than stranding you on the kana page. */
export function stageHref(node, stageIndex, stage) {
  const pool = stage?.review
    ? [...new Set([...(node.groups || []), ...priorGroups(node.id)])]
    : (node.groups || []);

  const params = new URLSearchParams({
    script: node.script,
    mode: stage?.mode || 'flashcard',
    lesson: node.id,
    stage: String(stageIndex),
    focus: '1',
  });
  if (pool.length) params.set('groups', pool.join(','));
  if (stage?.review) params.set('review', '1');

  return `/kana?${params.toString()}`;
}

export function nodeHref(node, section, lesson) {
  if (node.route) return node.route;

  if (node.script) {
    const stages = lesson?.stages || [];
    // nextStage is -1 once every stage is banked; replaying the last one is
    // the sensible thing to do for a finished row.
    const raw = typeof lesson?.nextStage === 'number' ? lesson.nextStage : 0;
    const index = raw >= 0 ? raw : Math.max(0, stages.length - 1);
    return stageHref(node, index, stages[index]);
  }

  return section?.route || '/dashboard';
}
